import { useState } from "react";
import { Link } from "react-router-dom";
import InfoHint from "../components/InfoHint";
import { fmtEUR } from "../lib/auth";
import { useGoalTree, useHousehold, useRecs } from "./data";
import type { WidgetComponentProps, WidgetEditControlsProps } from "./registry";

/** Special `hidden`-key for the Sparbaum-Stand tile (no account id). */
const SPARBAUM_KEY = "sparbaum";

function hiddenList(config: Record<string, unknown>): (number | string)[] {
  return Array.isArray(config.hidden) ? (config.hidden as (number | string)[]) : [];
}

export default function KontenWidget({ config, size }: WidgetComponentProps) {
  const recs = useRecs();
  const tree = useGoalTree();
  const hh = useHousehold();
  const hidden = hiddenList(config);
  const umbrella = tree?.[0];
  const boatTotal = umbrella ? Number(umbrella.total) : 0;
  const longGoal = hh?.long_term_goal_name ?? umbrella?.name ?? "Ziel";
  return (
    <section>
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="section-title">Kontostand</h2>
        <Link to="/reconcile" className="text-xs text-brand-300 hover:underline">
          Abgleich →
        </Link>
      </div>
      <div
        className={
          "grid gap-3 grid-cols-2 " +
          (size === "half" ? "" : "sm:grid-cols-2 lg:grid-cols-3")
        }
      >
        {(recs ?? [])
          .filter((r) => !hidden.includes(r.acc.id))
          .map((r) => (
            <KontoTile
              key={r.acc.id}
              label={r.acc.name}
              balance={Number(r.rec.kontostand)}
              buffer={Number(r.rec.buffer)}
            />
          ))}
        {umbrella && !hidden.includes(SPARBAUM_KEY) && (
          <KontoTile
            label={longGoal}
            balance={boatTotal}
            subtitle="Sparbaum-Stand"
            tone="brand"
          />
        )}
      </div>
    </section>
  );
}

/** Edit-mode header control: pick which Konten (und der Sparbaum-Stand)
 *  appear. Stored as a hide-list so newly added accounts show up by default;
 *  an empty selection drops the key entirely, keeping an untouched widget
 *  equal to the built-in default layout. */
export function KontenEditControls({ config, onConfig }: WidgetEditControlsProps) {
  const recs = useRecs();
  const [open, setOpen] = useState(false);
  const hidden = hiddenList(config);
  const entries: { key: number | string; label: string }[] = [
    ...(recs ?? []).map((r) => ({ key: r.acc.id, label: r.acc.name })),
    { key: SPARBAUM_KEY, label: "Sparbaum-Stand" },
  ];
  const shown = entries.filter((e) => !hidden.includes(e.key)).length;

  function toggle(key: number | string) {
    const next = hidden.includes(key)
      ? hidden.filter((k) => k !== key)
      : [...hidden, key];
    const { hidden: _drop, ...rest } = config;
    onConfig(next.length ? { ...rest, hidden: next } : rest);
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="px-1.5 py-0.5 rounded text-[11px] border border-ink-700 text-ink-300 hover:border-brand-500 hover:text-brand-300"
        title="Welche Konten angezeigt werden"
      >
        Konten {shown}/{entries.length} ▾
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-20 w-56 max-h-64 overflow-y-auto rounded-lg border border-ink-700 bg-ink-900 p-1.5 shadow-cardHover">
            {recs === null && (
              <p className="px-2 py-1 text-[11px] text-ink-400">Lädt…</p>
            )}
            {entries.map((e) => (
              <label
                key={String(e.key)}
                className="flex items-center gap-2 rounded px-2 py-1.5 text-xs text-ink-200 hover:bg-ink-800 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={!hidden.includes(e.key)}
                  onChange={() => toggle(e.key)}
                  className="accent-brand-500"
                />
                <span className="truncate">{e.label}</span>
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function KontoTile({
  label,
  balance,
  buffer,
  subtitle,
  tone,
}: {
  label: string;
  balance: number;
  buffer?: number;
  subtitle?: string;
  tone?: "brand";
}) {
  const bg =
    tone === "brand" ? "bg-brand-700/30 border-brand-700 text-ink-100" : "card";
  return (
    <div className={"rounded-xl border p-4 " + bg}>
      <div className="label text-ink-400">{label}</div>
      <div className="mt-1 text-2xl font-semibold num">{fmtEUR(balance)}</div>
      {buffer !== undefined && (
        <div className="mt-1 text-xs num flex items-center gap-1">
          <span className="text-ink-400">Spielraum </span>
          <span className={buffer < 0 ? "text-bad-500" : "text-ink-200"}>{fmtEUR(buffer)}</span>
          <InfoHint
            label="Wie wird der Spielraum berechnet?"
            text={
              <>
                Kontostand minus alles, was in den Umschlägen dieses Kontos eingeplant ist
                (Budgets + Überträge aus Vormonaten − zugeordnete Ausgaben). Negativ
                bedeutet: In den Umschlägen ist mehr eingeplant, als tatsächlich auf dem
                Konto liegt.
                <span className="block mt-1.5 text-ink-400">
                  Beispiel: 2.000 € auf dem Konto, aber 4.000 € in den Umschlägen (2 Monate
                  à 2.000 € Budget, noch nichts zugeordnet) → Spielraum = −2.000 €.
                </span>
              </>
            }
          />
        </div>
      )}
      {subtitle && <div className="mt-1 text-xs text-ink-400">{subtitle}</div>}
    </div>
  );
}
